import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { config } from "../Environment";
import useAuth from "../hooks/useAuth";
import useRetryFetch from "../hooks/useRetryFetch";
import { Button } from "./ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { CheckCircle2, Loader2, Users, AlertCircle } from "lucide-react";
import { toast } from "sonner";

function JoinCollection() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, auth } = useAuth();
  const retryFetch = useRetryFetch();

  const collectionId = searchParams.get("collection");
  const [collectionInfo, setCollectionInfo] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [joined, setJoined] = useState(false);
  const [error, setError] = useState("");

  const getLevelLabel = (level: number) => {
    if (level === 10) return t("department") || "Department";
    if (level === 20) return t("course") || "Course";
    if (level === 30) return t("class") || "Class";
    return t("group") || "Group";
  };

  useEffect(() => {
    if (!collectionId) {
      setError(t("invalid_join_link") || "Invalid join link");
      setLoading(false);
      return;
    }

    // Fetch collection info
    const fetchCollectionInfo = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${config.API_BASE}/api/collections/info/${collectionId}`);
        if (response.ok) {
          const data = await response.json();
          setCollectionInfo(data);
        } else {
          setError(t("collection_not_found") || "Collection not found");
        }
      } catch (err) {
        setError(t("failed_to_load_collection") || "Failed to load collection information");
      } finally {
        setLoading(false);
      }
    };

    fetchCollectionInfo();
  }, [collectionId, t]);

  const handleJoin = async () => {
    if (!auth?.accessToken) {
      // Redirect to login with return URL
      navigate(`/login?return=${encodeURIComponent(`/join-collection?collection=${collectionId}`)}`);
      return;
    }

    try {
      setJoining(true);
      const response = await retryFetch(`${config.API_BASE}/api/collection/${collectionId}/join`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      });

      if (response.ok) {
        const data = await response.json();
        setJoined(true);

        if (data.alreadyMember) {
          toast.success(t("already_member_of_collection") || "You are already a member of this collection");
        } else {
          toast.success(t("successfully_joined_collection") || "Successfully joined collection");
        }

        // Redirect to portal after 2 seconds
        setTimeout(() => {
          navigate("/portal");
        }, 2000);
      } else {
        const errorData = await response.json();
        setError(errorData.error || t("failed_to_join_collection") || "Failed to join collection");
        toast.error(errorData.error || t("failed_to_join_collection") || "Failed to join collection");
      }
    } catch (err: any) {
      setError(err.message || t("failed_to_join_collection") || "Failed to join collection");
      toast.error(err.message || t("failed_to_join_collection") || "Failed to join collection");
    } finally {
      setJoining(false);
    }
  };

  if (loading) {
    return (
      <div className="h-full w-full flex items-center justify-center py-12">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <span className="text-muted-foreground">{t("loading")}</span>
        </div>
      </div>
    );
  }

  if (error && !collectionInfo) {
    return (
      <div className="h-full w-full flex items-center justify-center py-12">
        <div className="mx-auto grid w-full max-w-[400px] gap-6 px-4">
          <Card className="border-red-200">
            <CardHeader>
              <div className="flex items-center gap-2 text-red-600">
                <AlertCircle className="h-5 w-5" />
                <CardTitle>{t("error") || "Error"}</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-4">{error}</p>
              <Button onClick={() => navigate("/login")} className="w-full">
                {t("go_to_login") || "Go to Login"}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full w-full flex items-center justify-center py-12">
      <div className="mx-auto grid w-full max-w-[500px] gap-6 px-4">
        <div className="grid gap-2 text-center">
          <h1 className="text-3xl font-bold">{t("join_collection") || "Join Collection"}</h1>
        </div>

        {collectionInfo && (
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3 mb-2">
                <div className="p-3 rounded-lg bg-primary/10">
                  <Users className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <CardTitle>{collectionInfo.name}</CardTitle>
                  <CardDescription>
                    {getLevelLabel(collectionInfo.level)}
                    {collectionInfo.organization && ` • ${collectionInfo.organization}`}
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {!auth?.accessToken ? (
                <>
                  <p className="text-sm text-muted-foreground">
                    {t("login_to_join_collection") || "Please log in to join this collection. If you don't have an account, you can sign up."}
                  </p>
                  <div className="flex flex-col gap-3">
                    <Button onClick={handleJoin} className="w-full">
                      {t("login_to_join") || "Log In to Join"}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => navigate(`/register?collection=${collectionId}`)}
                      className="w-full"
                    >
                      {t("sign_up_to_join") || "Sign Up to Join"}
                    </Button>
                  </div>
                </>
              ) : joined ? (
                <div className="text-center py-6">
                  <CheckCircle2 className="h-12 w-12 text-green-600 mx-auto mb-3" />
                  <p className="font-semibold text-lg mb-2">
                    {t("successfully_joined") || "Successfully Joined!"}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {t("redirecting_to_portal") || "Redirecting to your portal..."}
                  </p>
                </div>
              ) : (
                <>
                  <p className="text-sm text-muted-foreground">
                    {t("join_collection_description") || "You will be added to this collection and gain access to shared activities and resources."}
                  </p>
                  {error && (
                    <div className="p-3 text-sm text-red-600 border border-red-200 rounded bg-red-50">
                      {error}
                    </div>
                  )}
                  <div className="flex flex-col gap-3">
                    <Button
                      onClick={handleJoin}
                      disabled={joining}
                      className="w-full"
                    >
                      {joining ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          {t("joining") || "Joining..."}
                        </>
                      ) : (
                        t("join_now") || "Join Now"
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => navigate("/portal")}
                      className="w-full"
                    >
                      {t("cancel") || "Cancel"}
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

export default JoinCollection;
